import React, { useCallback, useEffect, useMemo, useRef, useState } from "react";
import {
  Row,
  Col,
  Card,
  Statistic,
  Avatar,
  Button,
  message,
  Spin,
  Dropdown,
  Menu,
  Tag,
  Input,
  Tooltip,
  Empty,
  Progress,
} from "antd";
import {
  TrendingUp as RiseOutlined,
  MoreHorizontal as EllipsisOutlined,
  CheckCircle as CheckCircleOutlined,
  XCircle as CloseCircleOutlined,
  Home as BedOutlined,
  User as UserOutlined,
  Activity as LineChartOutlined,
  BarChart2 as BarChartOutlined,
  Calendar as CalendarOutlined,
  Maximize as FullscreenOutlined,
  Minimize as FullscreenExitOutlined,
  Zap as ThunderboltOutlined,
  CreditCard as WalletOutlined,
  Home as HomeOutlined,
  Percent as PercentageOutlined,
} from "react-feather";
import DatePicker from "react-bootstrap-daterangepicker";
import moment from "moment";
import { useNavigate, Link } from "react-router-dom";
import axios from "axios";
import "bootstrap/dist/css/bootstrap.min.css";
import "bootstrap-daterangepicker/daterangepicker.css";

const { Search } = Input;

const formatINR = (value) => {
  const n = Number(value || 0);
  return n.toLocaleString("en-IN", {
    maximumFractionDigits: 0,
  });
};

const statusColor = (statusRaw) => {
  const status = (statusRaw || "").toLowerCase();
  if (status === "confirmed") return "blue";
  if (status === "checked_in" || status === "checked in") return "green";
  if (status === "checked_out" || status === "checked out") return "gold";
  if (status === "reserved") return "orange";
  if (status === "cancelled" || status === "canceled") return "red";
  return "default";
};

const statusLabel = (statusRaw) =>
  String(statusRaw || "reserved")
    .replace(/_/g, " ")
    .replace(/\b\w/g, (s) => s.toUpperCase());

const normalizeStatus = (statusRaw) => String(statusRaw || "").toLowerCase();

const MiniTrend = ({ title, icon, data, valueKey, prefix = "", suffix = "", emptyText }) => {
  const safeData = Array.isArray(data) ? data.slice(-10) : [];
  const max = Math.max(...safeData.map((row) => Number(row[valueKey] || 0)), 0);

  return (
    <div className="ns-be-trend-card">
      <div className="ns-be-panel-mini-head">
        <span>{icon}</span>
        <strong>{title}</strong>
      </div>

      {safeData.length ? (
        <div className="ns-be-trend-bars">
          {safeData.map((row, index) => {
            const value = Number(row[valueKey] || 0);
            const height = max > 0 ? Math.max(10, Math.round((value / max) * 100)) : 10;

            return (
              <Tooltip
                key={`${row.date || index}-${index}`}
                title={`${row.date || "Date"}: ${prefix}${formatINR(value)}${suffix}`}
              >
                <div className="ns-be-bar-wrap">
                  <div className="ns-be-bar" style={{ height: `${height}%` }} />
                </div>
              </Tooltip>
            );
          })}
        </div>
      ) : (
        <div className="ns-be-chart-empty">{emptyText || "No trend data available."}</div>
      )}
    </div>
  );
};

const MetricCard = ({ title, value, prefix, suffix, icon, tone, note }) => (
  <Card className={`ns-be-metric ns-be-metric-${tone}`} bodyStyle={{ padding: 0 }}>
    <div className="ns-be-metric-inner">
      <div className="ns-be-metric-top">
        <div className="ns-be-metric-icon">{icon}</div>
        <div className="ns-be-metric-signal" />
      </div>

      <Statistic
        title={title}
        value={value}
        precision={0}
        prefix={prefix}
        suffix={suffix}
      />

      <div className="ns-be-metric-note">{note}</div>
    </div>
  </Card>
);

const BookingEngineDashboard = () => {
  const navigate = useNavigate();
  const [roomTypes, setRoomTypes] = useState([]);
  const [activeRoomTypes, setActiveRoomTypes] = useState([]);
  const [filteredRoomTypes, setFilteredRoomTypes] = useState([]);
  const [reservations, setReservations] = useState([]);
  const [trends, setTrends] = useState({
    reservations: [],
    revenue: [],
  });
  const [summary, setSummary] = useState({
    totalReservations: 0,
    totalRevenue: 0,
    avgOccupancy: 0,
  });
  const [loading, setLoading] = useState(true);
  const [searchText, setSearchText] = useState("");
  const [dateRange, setDateRange] = useState({
    startDate: moment().startOf("month"),
    endDate: moment(),
  });
  const maximizeBtnRef = useRef(null);
  const [isFullScreen, setFullScreen] = useState(false);

  /* ------ Full Screen Toggle ------ */
  useEffect(() => {
    const handleClick = () => {
      if (!document.fullscreenElement) {
        document.documentElement.requestFullscreen().catch((err) => {
          console.error("Failed to enter fullscreen:", err);
        });
        setFullScreen(true);
      } else {
        document.exitFullscreen().catch((err) => {
          console.error("Failed to exit fullscreen:", err);
        });
        setFullScreen(false);
      }
    };

    const handleFullscreenChange = () => {
      setFullScreen(!!document.fullscreenElement);
    };

    const maximizeBtn = maximizeBtnRef.current;
    if (maximizeBtn) {
      maximizeBtn.addEventListener("click", handleClick);
    }

    document.addEventListener("fullscreenchange", handleFullscreenChange);

    return () => {
      if (maximizeBtn) {
        maximizeBtn.removeEventListener("click", handleClick);
      }
      document.removeEventListener("fullscreenchange", handleFullscreenChange);

      if (document.fullscreenElement) {
        document.exitFullscreen().catch((err) => {
          console.error("Failed to exit fullscreen during cleanup:", err);
        });
      }
    };
  }, []);

  const fetchDashboard = useCallback(async () => {
    const apiUrl = process.env.REACT_APP_API_URL;
    const token = localStorage.getItem("token");

    if (!apiUrl || !token) {
      message.error("Missing API URL or auth token.");
      setLoading(false);
      return;
    }

    setLoading(true);

    try {
      const { startDate, endDate } = dateRange;

      const response = await axios.get(`${apiUrl}/booking-engine/dashboard`, {
        headers: {
          Authorization: `Bearer ${token}`,
        },
        params: {
          startDate: startDate.format("YYYY-MM-DD"),
          endDate: endDate.format("YYYY-MM-DD"),
        },
      });

      const payload = response.data || {};
      if (!payload.success) {
        throw new Error(payload.message || "Failed to fetch dashboard data.");
      }

      const data = payload.data || {};

      const roomTypesFromApi = (data.roomTypes || []).map((rt, index) => ({
        id: rt.id || rt.room_type_id || index + 1,
        name: rt.name || rt.type_name || "Room Type",
        type: "Room Type",
        status:
          typeof rt.status === "boolean"
            ? rt.status
              ? "active"
              : "inactive"
            : rt.status || "active",
        reservations: Number(rt.reservations || 0),
        revenue: Number(rt.revenue || 0),
        occupancy: Number(rt.occupancy || 0),
        lastUpdated: rt.lastUpdated
          ? moment(rt.lastUpdated)
          : rt.updated_at
          ? moment(rt.updated_at)
          : null,
        logo: rt.logo || null,
        description: rt.description || "",
        totalRooms: Number(rt.totalRooms || rt.total_rooms || 0),
      }));

      setRoomTypes(roomTypesFromApi);

      const active = roomTypesFromApi.filter((rt) => rt.status === "active");
      setActiveRoomTypes(active);
      setFilteredRoomTypes(
        active.filter((rt) =>
          String(rt.name || "").toLowerCase().includes(searchText.toLowerCase())
        )
      );

      const reservationsFromApi = (data.reservations || []).map((r, index) => ({
        id: r.id || r.reservation_id || index + 1,
        guest: r.guestName || r.guest || "Guest",
        roomType: r.roomType || r.room_type || "",
        amount: Number(r.amount || r.totalAmount || 0),
        date: r.date ? moment(r.date) : moment(),
        status: r.status || "reserved",
        checkInDate: r.checkInDate
          ? moment(r.checkInDate)
          : r.check_in_date
          ? moment(r.check_in_date)
          : null,
        checkOutDate: r.checkOutDate
          ? moment(r.checkOutDate)
          : r.check_out_date
          ? moment(r.check_out_date)
          : null,
        nights: Number(r.nights || 0),
        adults: r.adults ?? null,
        children: r.children ?? null,
      }));

      setReservations(reservationsFromApi);

      const resTrend = (data.trends?.reservations || []).map((row) => ({
        date: row.date,
        reservations: Number(row.reservations || row.count || 0),
        roomType: row.roomType || row.room_type || "",
      }));

      const revTrend = (data.trends?.revenue || []).map((row) => ({
        date: row.date,
        revenue: Number(row.revenue || 0),
        roomType: row.roomType || row.room_type || "",
      }));

      setTrends({
        reservations: resTrend,
        revenue: revTrend,
      });

      const summaryFromApi = data.summary || {};

      const computedReservations = reservationsFromApi.length;
      const computedRevenue = reservationsFromApi.reduce(
        (sum, r) => sum + (r.amount || 0),
        0
      );
      const computedOccupancy =
        roomTypesFromApi.length > 0
          ? Math.round(
              (roomTypesFromApi.reduce(
                (sum, rt) => sum + (rt.occupancy || 0),
                0
              ) /
                roomTypesFromApi.length) *
                100
            ) / 100
          : 0;

      const totalReservations =
        summaryFromApi.totalReservations && summaryFromApi.totalReservations > 0
          ? Number(summaryFromApi.totalReservations)
          : computedReservations;

      const totalRevenue =
        summaryFromApi.totalRevenue && summaryFromApi.totalRevenue > 0
          ? Number(summaryFromApi.totalRevenue)
          : computedRevenue;

      const avgOccupancy =
        summaryFromApi.avgOccupancy && summaryFromApi.avgOccupancy > 0
          ? Number(summaryFromApi.avgOccupancy)
          : computedOccupancy;

      setSummary({
        totalReservations,
        totalRevenue,
        avgOccupancy,
      });
    } catch (error) {
      console.error("Dashboard fetch error:", error);
      message.error(
        error.response?.data?.message ||
          error.message ||
          "Failed to fetch dashboard data."
      );
    } finally {
      setLoading(false);
    }
  }, [dateRange, searchText]);

  useEffect(() => {
    fetchDashboard();
  }, [fetchDashboard]);

  useEffect(() => {
    const filtered = activeRoomTypes.filter((rt) =>
      String(rt.name || "").toLowerCase().includes(searchText.toLowerCase())
    );
    setFilteredRoomTypes(filtered);
  }, [searchText, activeRoomTypes]);

  const totalReservations = summary.totalReservations || 0;
  const totalRevenue = summary.totalRevenue || 0;
  const avgOccupancy = summary.avgOccupancy || 0;
  const avgBookingValue =
    totalReservations > 0 ? Math.round(totalRevenue / totalReservations) : 0;

  const activeReservationCount = useMemo(
    () =>
      reservations.filter((reservation) => {
        const status = normalizeStatus(reservation.status);
        return status !== "cancelled" && status !== "canceled";
      }).length,
    [reservations]
  );

  const cancelledReservationCount = useMemo(
    () =>
      reservations.filter((reservation) => {
        const status = normalizeStatus(reservation.status);
        return status === "cancelled" || status === "canceled";
      }).length,
    [reservations]
  );

  const bestRoomType = useMemo(() => {
    if (!activeRoomTypes.length) return null;

    return [...activeRoomTypes].sort((a, b) => {
      const bScore = Number(b.revenue || 0) || Number(b.reservations || 0);
      const aScore = Number(a.revenue || 0) || Number(a.reservations || 0);
      return bScore - aScore;
    })[0];
  }, [activeRoomTypes]);

  const conversionSignal = totalReservations > 0 ? "Receiving direct demand" : "No direct bookings yet";
  const activeRoomPercent =
    roomTypes.length > 0 ? Math.round((activeRoomTypes.length / roomTypes.length) * 100) : 0;

  const handleActivate = (roomType) => {
    message.info(`Activation triggered for ${roomType.name}`);
    setRoomTypes((prev) =>
      prev.map((rt) =>
        rt.id === roomType.id
          ? { ...rt, status: "active", lastUpdated: moment() }
          : rt
      )
    );
  };

  const handleDeactivate = (roomType) => {
    setRoomTypes((prev) =>
      prev.map((rt) =>
        rt.id === roomType.id
          ? { ...rt, status: "inactive", lastUpdated: moment() }
          : rt
      )
    );
    message.success(`${roomType.name} deactivated`);
  };

  const handleEdit = (roomType) => {
    navigate(`/booking-engine/room-types/edit/${roomType.id}`);
  };

  const getMenu = (roomType) => (
    <Menu>
      {roomType.status !== "active" && (
        <Menu.Item key="activate" onClick={() => handleActivate(roomType)}>
          Activate
        </Menu.Item>
      )}
      {roomType.status === "active" && (
        <Menu.Item
          key="deactivate"
          danger
          onClick={() => handleDeactivate(roomType)}
        >
          Deactivate
        </Menu.Item>
      )}
    </Menu>
  );

  const handleDateChange = (param) => {
    if (param.start && param.end) {
      setDateRange({
        startDate: moment(param.start),
        endDate: moment(param.end),
      });
    }
  };

  const renderReservationStay = (item) => {
    const checkIn = item.checkInDate || item.date || moment();
    const checkOut = item.checkOutDate || null;

    const nightsText =
      item.nights && item.nights > 0 ? `${item.nights} Night${item.nights === 1 ? "" : "s"}` : "";

    const paxParts = [];
    if (item.adults != null) {
      paxParts.push(`${item.adults} Adult${item.adults === 1 ? "" : "s"}`);
    }
    if (item.children != null) {
      paxParts.push(`${item.children} Child${item.children === 1 ? "" : "ren"}`);
    }

    return (
      <>
        <div className="ns-be-stay-date">
          {checkIn.format("DD MMM")}
          {checkOut ? ` → ${checkOut.format("DD MMM YYYY")}` : ""}
        </div>
        <div className="ns-be-stay-meta">
          {[nightsText, paxParts.join(", ")].filter(Boolean).join(" · ") || "Stay details pending"}
        </div>
      </>
    );
  };

  if (loading) {
    return (
      <div className="page-wrapper-full">
        <style>{`
          .ns-be-loading-screen {
            min-height: 100vh;
            display: flex;
            align-items: center;
            justify-content: center;
            background:
              radial-gradient(circle at 20% 20%, rgba(37, 99, 235, 0.12), transparent 28%),
              linear-gradient(135deg, #f8fbff 0%, #ffffff 55%, #f8fafc 100%);
          }

          .ns-be-loading-card {
            background: rgba(255,255,255,0.92);
            border: 1px solid rgba(15, 23, 42, 0.08);
            border-radius: 22px;
            box-shadow: 0 18px 50px rgba(15, 23, 42, 0.10);
            padding: 28px 34px;
            display: inline-flex;
            gap: 14px;
            align-items: center;
            color: #64748b;
            font-weight: 900;
          }
        `}</style>
        <div className="ns-be-loading-screen">
          <div className="ns-be-loading-card">
            <Spin size="large" />
            <span>Loading booking engine performance...</span>
          </div>
        </div>
      </div>
    );
  }

  return (
    <div className="page-wrapper-full">
      <style>{`
        .ns-be-dashboard {
          min-height: calc(100vh - 70px);
          background:
            radial-gradient(circle at 12% 4%, rgba(37, 99, 235, 0.08), transparent 28%),
            radial-gradient(circle at 82% 0%, rgba(14, 165, 233, 0.10), transparent 26%),
            #f8fafc;
          padding-bottom: 24px;
        }

        .ns-be-hero {
          position: relative;
          overflow: hidden;
          background:
            radial-gradient(circle at top left, rgba(37, 99, 235, 0.20), transparent 34%),
            radial-gradient(circle at top right, rgba(16, 185, 129, 0.12), transparent 30%),
            linear-gradient(135deg, #ffffff 0%, #f8fbff 100%);
          border: 1px solid rgba(15, 23, 42, 0.08);
          border-radius: 24px;
          box-shadow: 0 18px 48px rgba(15, 23, 42, 0.08);
          padding: 22px;
          margin-bottom: 18px;
        }

        .ns-be-hero::after {
          content: "";
          position: absolute;
          right: -80px;
          top: -80px;
          width: 220px;
          height: 220px;
          border-radius: 999px;
          background: rgba(37, 99, 235, 0.08);
          pointer-events: none;
        }

        .ns-be-hero-content {
          position: relative;
          z-index: 1;
          display: flex;
          justify-content: space-between;
          gap: 18px;
          flex-wrap: wrap;
          align-items: flex-start;
        }

        .ns-be-eyebrow {
          color: #2563eb;
          font-size: 0.74rem;
          letter-spacing: 0.14em;
          text-transform: uppercase;
          font-weight: 900;
          margin-bottom: 7px;
        }

        .ns-be-title-row {
          display: flex;
          align-items: center;
          gap: 10px;
          flex-wrap: wrap;
        }

        .ns-be-title {
          color: #001f5b;
          font-size: 1.62rem;
          line-height: 1.15;
          font-weight: 900;
          margin: 0;
        }

        .ns-be-live-pill {
          display: inline-flex;
          align-items: center;
          gap: 7px;
          border-radius: 999px;
          background: #ecfdf5;
          color: #047857;
          border: 1px solid #bbf7d0;
          padding: 5px 10px;
          font-size: 0.72rem;
          text-transform: uppercase;
          letter-spacing: 0.08em;
          font-weight: 900;
        }

        .ns-be-live-dot {
          width: 8px;
          height: 8px;
          border-radius: 999px;
          background: #10b981;
          box-shadow: 0 0 0 5px rgba(16, 185, 129, 0.12);
        }

        .ns-be-subtitle {
          color: #64748b;
          font-size: 0.9rem;
          line-height: 1.6;
          margin: 9px 0 0;
          max-width: 760px;
        }

        .ns-be-hero-pills {
          display: flex;
          flex-wrap: wrap;
          gap: 8px;
          margin-top: 14px;
        }

        .ns-be-pill {
          display: inline-flex;
          align-items: center;
          gap: 7px;
          border-radius: 999px;
          background: #ffffff;
          border: 1px solid rgba(15, 23, 42, 0.08);
          color: #334155;
          box-shadow: 0 8px 20px rgba(15, 23, 42, 0.04);
          padding: 7px 11px;
          font-size: 0.78rem;
          font-weight: 900;
          white-space: nowrap;
        }

        .ns-be-controls {
          display: flex;
          justify-content: flex-end;
          gap: 10px;
          flex-wrap: wrap;
          align-items: center;
        }

        .ns-be-date-trigger {
          display: inline-flex;
          align-items: center;
          gap: 9px;
          height: 42px;
          border-radius: 14px;
          background: #ffffff;
          border: 1px solid rgba(15, 23, 42, 0.10);
          box-shadow: 0 8px 22px rgba(15, 23, 42, 0.06);
          padding: 0 12px;
          cursor: pointer;
        }

        .ns-be-date-trigger input {
          border: 0 !important;
          background: transparent !important;
          box-shadow: none !important;
          width: 210px;
          padding: 0;
          color: #0f172a;
          font-weight: 900;
          cursor: pointer;
        }

        .ns-be-icon-btn {
          width: 42px;
          height: 42px;
          border-radius: 14px !important;
          border: 1px solid rgba(15, 23, 42, 0.10) !important;
          background: #ffffff !important;
          color: #1d4ed8 !important;
          display: inline-flex !important;
          align-items: center;
          justify-content: center;
          box-shadow: 0 8px 22px rgba(15, 23, 42, 0.06);
        }

        .ns-be-kpis {
          margin-bottom: 18px;
        }

        .ns-be-metric {
          border: 0 !important;
          border-radius: 20px !important;
          overflow: hidden;
          min-height: 172px;
          box-shadow: 0 12px 34px rgba(15, 23, 42, 0.08);
          transition: transform 0.18s ease, box-shadow 0.18s ease;
        }

        .ns-be-metric:hover {
          transform: translateY(-2px);
          box-shadow: 0 18px 44px rgba(15, 23, 42, 0.12);
        }

        .ns-be-metric-inner {
          padding: 17px;
          min-height: 172px;
          display: flex;
          flex-direction: column;
          justify-content: space-between;
          border: 1px solid rgba(15, 23, 42, 0.07);
          border-radius: 20px;
        }

        .ns-be-metric-blue .ns-be-metric-inner {
          background: linear-gradient(135deg, #eff6ff 0%, #ffffff 58%, #f8fbff 100%);
        }

        .ns-be-metric-green .ns-be-metric-inner {
          background: linear-gradient(135deg, #ecfdf5 0%, #ffffff 58%, #f7fffb 100%);
        }

        .ns-be-metric-orange .ns-be-metric-inner {
          background: linear-gradient(135deg, #fff7ed 0%, #ffffff 58%, #fffaf5 100%);
        }

        .ns-be-metric-purple .ns-be-metric-inner {
          background: linear-gradient(135deg, #faf5ff 0%, #ffffff 58%, #fdfbff 100%);
        }

        .ns-be-metric-top {
          display: flex;
          align-items: center;
          justify-content: space-between;
        }

        .ns-be-metric-icon {
          width: 42px;
          height: 42px;
          border-radius: 15px;
          background: rgba(255, 255, 255, 0.92);
          border: 1px solid rgba(15, 23, 42, 0.07);
          display: inline-flex;
          align-items: center;
          justify-content: center;
          color: #1d4ed8;
          font-size: 1.1rem;
          box-shadow: 0 8px 18px rgba(15, 23, 42, 0.05);
        }

        .ns-be-metric-signal {
          width: 48px;
          height: 18px;
          border-radius: 999px;
          background:
            linear-gradient(90deg, rgba(37,99,235,0.08), rgba(37,99,235,0.24)),
            repeating-linear-gradient(90deg, transparent 0, transparent 7px, rgba(37,99,235,0.28) 8px, transparent 9px);
        }

        .ns-be-metric .ant-statistic-title {
          color: #64748b;
          font-size: 0.78rem;
          font-weight: 900;
          text-transform: uppercase;
          letter-spacing: 0.04em;
          margin-bottom: 4px;
        }

        .ns-be-metric .ant-statistic-content {
          color: #0f172a;
          font-weight: 900;
          font-size: 1.7rem;
        }

        .ns-be-metric-note {
          color: #64748b;
          font-size: 0.78rem;
          line-height: 1.45;
          margin-top: 8px;
        }

        .ns-be-panel {
          border: 0 !important;
          border-radius: 22px !important;
          box-shadow: 0 14px 38px rgba(15, 23, 42, 0.08);
          overflow: hidden;
          height: 100%;
        }

        .ns-be-panel .ant-card-head {
          border-bottom: 1px solid rgba(15, 23, 42, 0.08);
          background:
            radial-gradient(circle at top right, rgba(37, 99, 235, 0.08), transparent 28%),
            #ffffff;
        }

        .ns-be-panel .ant-card-head-title {
          color: #0f172a;
          font-weight: 900;
        }

        .ns-be-panel-subtitle {
          color: #64748b;
          font-size: 0.8rem;
          margin-top: 2px;
        }

        .ns-be-trend-grid {
          display: grid;
          grid-template-columns: repeat(2, minmax(0, 1fr));
          gap: 14px;
        }

        .ns-be-trend-card {
          background: #f8fafc;
          border: 1px solid rgba(15, 23, 42, 0.08);
          border-radius: 18px;
          padding: 14px;
          min-height: 232px;
        }

        .ns-be-panel-mini-head {
          display: flex;
          align-items: center;
          gap: 8px;
          color: #0f172a;
          font-weight: 900;
          margin-bottom: 14px;
        }

        .ns-be-panel-mini-head span {
          width: 34px;
          height: 34px;
          border-radius: 13px;
          background: #eff6ff;
          color: #1d4ed8;
          display: inline-flex;
          align-items: center;
          justify-content: center;
        }

        .ns-be-trend-bars {
          height: 160px;
          display: flex;
          gap: 8px;
          align-items: flex-end;
          padding: 8px 2px 0;
        }

        .ns-be-bar-wrap {
          flex: 1;
          min-width: 8px;
          height: 100%;
          display: flex;
          align-items: flex-end;
        }

        .ns-be-bar {
          width: 100%;
          border-radius: 999px 999px 4px 4px;
          background: linear-gradient(180deg, #2563eb 0%, #38bdf8 100%);
          box-shadow: 0 8px 18px rgba(37, 99, 235, 0.18);
          transition: height 0.22s ease;
        }

        .ns-be-chart-empty {
          min-height: 150px;
          display: flex;
          align-items: center;
          justify-content: center;
          color: #94a3b8;
          font-weight: 800;
          text-align: center;
        }

        .ns-be-intel-card {
          background:
            radial-gradient(circle at top left, rgba(37, 99, 235, 0.12), transparent 30%),
            linear-gradient(135deg, #ffffff 0%, #f8fbff 100%);
          border: 1px solid rgba(15, 23, 42, 0.08);
          border-radius: 18px;
          padding: 16px;
          margin-bottom: 14px;
        }

        .ns-be-intel-label {
          color: #2563eb;
          text-transform: uppercase;
          letter-spacing: 0.12em;
          font-size: 0.7rem;
          font-weight: 900;
          margin-bottom: 5px;
        }

        .ns-be-intel-value {
          color: #001f5b;
          font-size: 1.1rem;
          font-weight: 900;
          line-height: 1.25;
          margin-bottom: 5px;
        }

        .ns-be-intel-note {
          color: #64748b;
          font-size: 0.82rem;
          line-height: 1.5;
        }

        .ns-be-health-row {
          display: grid;
          gap: 12px;
        }

        .ns-be-health-item {
          background: #f8fafc;
          border: 1px solid rgba(15, 23, 42, 0.08);
          border-radius: 16px;
          padding: 12px;
        }

        .ns-be-health-head {
          display: flex;
          justify-content: space-between;
          gap: 8px;
          align-items: center;
          margin-bottom: 7px;
          color: #0f172a;
          font-weight: 900;
        }

        .ns-be-res-list {
          display: grid;
          gap: 10px;
          max-height: 426px;
          overflow: auto;
          padding-right: 4px;
        }

        .ns-be-res-item {
          display: grid;
          grid-template-columns: minmax(180px, 1.15fr) minmax(160px, 0.9fr) minmax(170px, 1fr) auto;
          gap: 12px;
          align-items: center;
          background: #ffffff;
          border: 1px solid rgba(15, 23, 42, 0.08);
          border-radius: 16px;
          padding: 12px;
          transition: transform 0.18s ease, box-shadow 0.18s ease;
        }

        .ns-be-res-item:hover {
          transform: translateY(-1px);
          box-shadow: 0 10px 24px rgba(15, 23, 42, 0.08);
        }

        .ns-be-guest {
          display: flex;
          align-items: center;
          gap: 10px;
          min-width: 0;
        }

        .ns-be-avatar {
          background: linear-gradient(135deg, #1d4ed8, #38bdf8) !important;
          color: #ffffff !important;
          flex: 0 0 auto;
        }

        .ns-be-guest-name {
          color: #0f172a;
          font-weight: 900;
          overflow: hidden;
          text-overflow: ellipsis;
          white-space: nowrap;
        }

        .ns-be-room-name {
          color: #001f5b;
          font-weight: 900;
          overflow: hidden;
          text-overflow: ellipsis;
          white-space: nowrap;
        }

        .ns-be-amount {
          color: #64748b;
          font-size: 0.78rem;
          margin-top: 2px;
        }

        .ns-be-stay-date {
          color: #0f172a;
          font-weight: 900;
          font-size: 0.86rem;
        }

        .ns-be-stay-meta {
          color: #64748b;
          font-size: 0.76rem;
          margin-top: 3px;
        }

        .ns-be-room-list {
          display: grid;
          gap: 10px;
          max-height: 426px;
          overflow: auto;
          padding-right: 4px;
        }

        .ns-be-room-card {
          background: #ffffff;
          border: 1px solid rgba(15, 23, 42, 0.08);
          border-radius: 16px;
          padding: 12px;
          display: flex;
          align-items: center;
          justify-content: space-between;
          gap: 12px;
          transition: transform 0.18s ease, box-shadow 0.18s ease;
        }

        .ns-be-room-card:hover {
          transform: translateY(-1px);
          box-shadow: 0 10px 24px rgba(15, 23, 42, 0.08);
        }

        .ns-be-room-left {
          display: flex;
          gap: 12px;
          align-items: center;
          min-width: 0;
        }

        .ns-be-room-avatar {
          border: 2px solid #e6f0ff;
          background: #eff6ff !important;
          color: #1d4ed8 !important;
          flex: 0 0 auto;
        }

        .ns-be-room-title {
          color: #001f5b;
          font-weight: 900;
          font-size: 0.96rem;
          overflow: hidden;
          text-overflow: ellipsis;
          white-space: nowrap;
        }

        .ns-be-room-meta {
          color: #64748b;
          font-size: 0.76rem;
          line-height: 1.45;
          margin-top: 2px;
        }

        .ns-be-room-right {
          display: flex;
          align-items: center;
          gap: 8px;
          flex: 0 0 auto;
        }

        .ns-be-status-tag {
          border-radius: 999px !important;
          font-weight: 900;
          padding: 4px 8px !important;
          margin-right: 0 !important;
          cursor: pointer;
        }

        .ns-be-panel-actions {
          display: flex;
          align-items: center;
          gap: 8px;
          flex-wrap: wrap;
          justify-content: flex-end;
        }

        .ns-be-search {
          border-radius: 12px;
        }

        .ns-be-search .ant-input-affix-wrapper,
        .ns-be-search.ant-input-affix-wrapper {
          border-radius: 12px !important;
        }

        .ns-be-empty {
          min-height: 180px;
          display: flex;
          align-items: center;
          justify-content: center;
        }

        @media (max-width: 1200px) {
          .ns-be-trend-grid {
            grid-template-columns: 1fr;
          }

          .ns-be-res-item {
            grid-template-columns: 1fr;
            align-items: flex-start;
          }
        }

        @media (max-width: 768px) {
          .ns-be-dashboard {
            padding-bottom: 16px;
          }

          .ns-be-hero {
            padding: 16px;
          }

          .ns-be-title {
            font-size: 1.32rem;
          }

          .ns-be-controls {
            width: 100%;
            justify-content: flex-start;
          }

          .ns-be-date-trigger input {
            width: 180px;
          }

          .ns-be-room-card {
            align-items: flex-start;
            flex-direction: column;
          }

          .ns-be-room-right {
            width: 100%;
            justify-content: flex-end;
          }
        }
      `}</style>

      <div className="content container-fluid ns-be-dashboard">
        <div className="ns-be-hero">
          <div className="ns-be-hero-content">
            <div>
              <div className="ns-be-eyebrow">AI-first direct booking performance</div>
              <div className="ns-be-title-row">
                <h3 className="ns-be-title">Booking Engine Overview</h3>
                <span className="ns-be-live-pill">
                  <span className="ns-be-live-dot" />
                  Live Performance
                </span>
              </div>
              <p className="ns-be-subtitle">
                Track direct reservations, revenue, occupancy, room-type performance, and stay activity for the selected period.
              </p>

              <div className="ns-be-hero-pills">
                <span className="ns-be-pill">
                  <ThunderboltOutlined /> {conversionSignal}
                </span>
                <span className="ns-be-pill">
                  <HomeOutlined /> {activeRoomTypes.length}/{roomTypes.length || 0} active room types
                </span>
                <span className="ns-be-pill">
                  <CalendarOutlined /> {dateRange.startDate.format("DD MMM YYYY")} – {dateRange.endDate.format("DD MMM YYYY")}
                </span>
              </div>
            </div>

            <div className="ns-be-controls">
              <DatePicker
                initialSettings={{
                  singleDatePicker: false,
                  showDropdowns: true,
                  startDate: dateRange.startDate.toDate(),
                  endDate: dateRange.endDate.toDate(),
                  ranges: {
                    Today: [moment().toDate(), moment().toDate()],
                    "This Week": [
                      moment().startOf("week").toDate(),
                      moment().endOf("week").toDate(),
                    ],
                    "This Month": [
                      moment().startOf("month").toDate(),
                      moment().endOf("month").toDate(),
                    ],
                    "Next Month": [
                      moment().add(1, "month").startOf("month").toDate(),
                      moment().add(1, "month").endOf("month").toDate(),
                    ],
                  },
                  locale: {
                    format: "DD MMM YYYY",
                    applyLabel: "Select",
                    cancelLabel: "Cancel",
                  },
                }}
                onCallback={(start, end) => handleDateChange({ start, end })}
              >
                <div className="ns-be-date-trigger">
                  <CalendarOutlined />
                  <input
                    type="text"
                    value={`${dateRange.startDate.format("DD MMM")} - ${dateRange.endDate.format("DD MMM YYYY")}`}
                    readOnly
                  />
                  <i className="fa fa-chevron-down" />
                </div>
              </DatePicker>

              <Tooltip title={isFullScreen ? "Exit full screen" : "Full screen mode"}>
                <Link
                  to="#"
                  className="ns-be-icon-btn"
                  id="collapse-header"
                  ref={maximizeBtnRef}
                >
                  {isFullScreen ? <FullscreenExitOutlined /> : <FullscreenOutlined />}
                </Link>
              </Tooltip>
            </div>
          </div>
        </div>

        <Row gutter={[16, 16]} className="ns-be-kpis">
          <Col xs={24} sm={12} xl={6}>
            <MetricCard
              tone="blue"
              title="Total Reservations"
              value={totalReservations}
              icon={<RiseOutlined />}
              note="Across all room types in this period"
            />
          </Col>

          <Col xs={24} sm={12} xl={6}>
            <MetricCard
              tone="green"
              title="Total Revenue"
              value={totalRevenue}
              prefix="₹"
              icon={<WalletOutlined />}
              note="Gross booking value in selected period"
            />
          </Col>

          <Col xs={24} sm={12} xl={6}>
            <MetricCard
              tone="orange"
              title="Avg. Occupancy"
              value={avgOccupancy}
              suffix="%"
              icon={<PercentageOutlined />}
              note="Average share of sold inventory"
            />
          </Col>

          <Col xs={24} sm={12} xl={6}>
            <MetricCard
              tone="purple"
              title="Avg. Booking Value"
              value={avgBookingValue}
              prefix="₹"
              icon={<BarChartOutlined />}
              note="Per reservation in selected period"
            />
          </Col>
        </Row>

        <Row gutter={[16, 16]} className="mb-4">
          <Col xs={24} xl={15}>
            <Card
              className="ns-be-panel"
              title={
                <div>
                  <div>Performance Trends</div>
                  <div className="ns-be-panel-subtitle">Reservations and revenue movement for the selected period</div>
                </div>
              }
            >
              <div className="ns-be-trend-grid">
                <MiniTrend
                  title="Reservation Signal"
                  icon={<LineChartOutlined />}
                  data={trends.reservations}
                  valueKey="reservations"
                  emptyText="No reservation trend data available."
                />
                <MiniTrend
                  title="Revenue Signal"
                  icon={<BarChartOutlined />}
                  data={trends.revenue}
                  valueKey="revenue"
                  prefix="₹"
                  emptyText="No revenue trend data available."
                />
              </div>
            </Card>
          </Col>

          <Col xs={24} xl={9}>
            <Card
              className="ns-be-panel"
              title={
                <div>
                  <div>Revenue Intelligence</div>
                  <div className="ns-be-panel-subtitle">Quick read on demand, inventory, and active direct booking setup</div>
                </div>
              }
            >
              <div className="ns-be-intel-card">
                <div className="ns-be-intel-label">Top Performing Room Type</div>
                <div className="ns-be-intel-value">
                  {bestRoomType ? bestRoomType.name : "No room type performance yet"}
                </div>
                <div className="ns-be-intel-note">
                  {bestRoomType
                    ? `${bestRoomType.reservations || 0} reservation(s), ₹${formatINR(bestRoomType.revenue || 0)} revenue, ${bestRoomType.occupancy || 0}% occupancy.`
                    : "Room type performance will appear after reservations start coming in."}
                </div>
              </div>

              <div className="ns-be-health-row">
                <div className="ns-be-health-item">
                  <div className="ns-be-health-head">
                    <span>Active Room Type Coverage</span>
                    <strong>{activeRoomPercent}%</strong>
                  </div>
                  <Progress percent={activeRoomPercent} showInfo={false} strokeColor="#2563eb" />
                </div>

                <div className="ns-be-health-item">
                  <div className="ns-be-health-head">
                    <span>Non-cancelled Reservations</span>
                    <strong>{activeReservationCount}</strong>
                  </div>
                  <div className="ns-be-intel-note">
                    Cancelled in this period: {cancelledReservationCount}
                  </div>
                </div>

                <div className="ns-be-health-item">
                  <div className="ns-be-health-head">
                    <span>Direct Booking Value</span>
                    <strong>₹{formatINR(totalRevenue)}</strong>
                  </div>
                  <div className="ns-be-intel-note">
                    Average booking value: ₹{formatINR(avgBookingValue)}
                  </div>
                </div>
              </div>
            </Card>
          </Col>
        </Row>

        <Row gutter={[16, 16]} className="mb-4">
          <Col xs={24} xl={14}>
            <Card
              className="ns-be-panel"
              title={
                <div>
                  <div>Recent Reservations</div>
                  <div className="ns-be-panel-subtitle">Latest guest bookings within the selected period</div>
                </div>
              }
              extra={<Tag color="blue">{reservations.length} booking(s)</Tag>}
            >
              {reservations.length === 0 ? (
                <div className="ns-be-empty">
                  <Empty description="No reservations in this period" />
                </div>
              ) : (
                <div className="ns-be-res-list">
                  {reservations.map((item) => (
                    <div className="ns-be-res-item" key={item.id}>
                      <div className="ns-be-guest">
                        <Avatar icon={<UserOutlined />} className="ns-be-avatar" />
                        <div style={{ minWidth: 0 }}>
                          <div className="ns-be-guest-name">{item.guest}</div>
                          <div className="ns-be-amount">₹{formatINR(item.amount)}</div>
                        </div>
                      </div>

                      <div>
                        <div className="ns-be-room-name">{item.roomType || "-"}</div>
                        <div className="ns-be-amount">Accommodation</div>
                      </div>

                      <div>{renderReservationStay(item)}</div>

                      <div className="text-end">
                        <Tag color={statusColor(item.status)} className="ns-be-status-tag">
                          {statusLabel(item.status)}
                        </Tag>
                      </div>
                    </div>
                  ))}
                </div>
              )}
            </Card>
          </Col>

          <Col xs={24} xl={10}>
            <Card
              className="ns-be-panel"
              title={
                <div>
                  <div>Room Types Overview</div>
                  <div className="ns-be-panel-subtitle">Active room types visible to the booking engine</div>
                </div>
              }
              extra={
                <div className="ns-be-panel-actions">
                  <Search
                    placeholder="Search room types"
                    onChange={(e) => setSearchText(e.target.value)}
                    value={searchText}
                    className="ns-be-search"
                    style={{ width: 220 }}
                    allowClear
                    size="middle"
                  />
                </div>
              }
            >
              {filteredRoomTypes.length === 0 ? (
                <div className="ns-be-empty">
                  <Empty description="No room types match your search" />
                </div>
              ) : (
                <div className="ns-be-room-list">
                  {filteredRoomTypes.map((rt) => (
                    <div className="ns-be-room-card" key={rt.id}>
                      <div className="ns-be-room-left">
                        <Avatar
                          src={rt.logo}
                          size={52}
                          icon={<BedOutlined />}
                          className="ns-be-room-avatar"
                        />
                        <div style={{ minWidth: 0 }}>
                          <div className="ns-be-room-title">{rt.name}</div>
                          <div className="ns-be-room-meta">
                            Occupancy {rt.occupancy || 0}% · Total rooms {rt.totalRooms || 0}
                          </div>
                          <div className="ns-be-room-meta">
                            Last updated: {rt.lastUpdated ? rt.lastUpdated.fromNow() : "N/A"}
                          </div>
                        </div>
                      </div>

                      <div className="ns-be-room-right">
                        <Tag
                          icon={
                            rt.status === "active" ? (
                              <CheckCircleOutlined />
                            ) : (
                              <CloseCircleOutlined />
                            )
                          }
                          color={rt.status === "active" ? "green" : "red"}
                          className="ns-be-status-tag"
                          onClick={() => handleEdit(rt)}
                        >
                          {String(rt.status || "").toUpperCase()}
                        </Tag>
                        <Dropdown overlay={getMenu(rt)} trigger={["click"]}>
                          <Button type="text" icon={<EllipsisOutlined />} />
                        </Dropdown>
                      </div>
                    </div>
                  ))}
                </div>
              )}
            </Card>
          </Col>
        </Row>
      </div>
    </div>
  );
};

export default BookingEngineDashboard;
